import { languages, type Languages } from '../i18n/i18n.language-config';

/** A geo point as both the account and agency-location records carry it. */
export interface GeoPoint {
  readonly lat: number;
  readonly lon: number;
}

/**
 * The unit a region measures in. Both spellings are what Elasticsearch's
 * `geo_distance` filter takes, so the same token labels the slider and goes to
 * the directory as the radius suffix, and nothing converts between the two: a
 * region measures, filters and displays in one unit throughout.
 */
export type DistanceUnit = 'mi' | 'km';

/**
 * The unit for each platform language. Britain measures distance in miles — road
 * signs and speedometers — so AU is the only metric platform.
 *
 * Typed against every platform language, so a new region is a compile error here
 * rather than an undefined unit reaching the directory as `50undefined`.
 */
const DISTANCE_UNIT_BY_LANGUAGE: Readonly<Record<Languages, DistanceUnit>> =
  Object.freeze({
    [languages['en-US']]: 'mi',
    [languages['en-GB']]: 'mi',
    [languages['en-AU']]: 'km',
  });

/** The unit written out, for the slider's aria-label. */
export const DISTANCE_UNIT_NAME: Readonly<Record<DistanceUnit, string>> =
  Object.freeze({ mi: 'miles', km: 'kilometres' });

/**
 * The fallback covers a region the type cannot: one reaching a stale bundle,
 * which still has to measure in something.
 */
export const getDistanceUnit = (language: Languages): DistanceUnit =>
  DISTANCE_UNIT_BY_LANGUAGE[language] ?? 'mi';

export interface RadiusRange {
  readonly min: number;
  readonly max: number;
  readonly step: number;
  /** Where the slider opens, and the radius a search uses before it is dragged. */
  readonly initial: number;
}

/**
 * The search radius slider, per unit. The metric range is the imperial one
 * converted and rounded to its step, not the same numbers relabelled: 500mi is
 * ~805km, so reusing 10-500 would have cut the widest AU search by ~38%.
 */
export const RADIUS_RANGE: Readonly<Record<DistanceUnit, RadiusRange>> =
  Object.freeze({
    mi: { min: 10, max: 500, step: 10, initial: 50 },
    km: { min: 20, max: 800, step: 20, initial: 80 },
  });

const EARTH_RADIUS: Readonly<Record<DistanceUnit, number>> = Object.freeze({
  mi: 3958.7613,
  km: 6371.0088,
});

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Great-circle distance in the given unit.
 *
 * Computed client-side rather than server-side: both ends are already in hand —
 * the policyholder's own `AccountDto.location` and each agency location's geo
 * point — so the radius filter and the per-row distance need no new endpoint.
 */
export const distanceBetween = (
  from: GeoPoint,
  to: GeoPoint,
  unit: DistanceUnit
): number => {
  const dLat = toRadians(to.lat - from.lat);
  const dLon = toRadians(to.lon - from.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS[unit] * 2 * Math.asin(Math.sqrt(a));
};

const isGeoPoint = (value: unknown): value is GeoPoint => {
  const point = value as GeoPoint | undefined;
  return typeof point?.lat === 'number' && typeof point?.lon === 'number';
};

/**
 * A measurement and the unit it was taken in, kept together.
 *
 * The region can resolve after a search has already been measured and listed —
 * a flag-driven environment gets it from GrowthBook, not the hostname — and a
 * bare number outlives the unit that produced it. Labelling it with whatever
 * unit is current then reports a distance measured in miles as kilometres,
 * having converted nothing. There is no second unit to pass here, so the label
 * and the figure cannot come apart.
 */
export interface Distance {
  readonly value: number;
  readonly unit: DistanceUnit;
}

/**
 * Distance from the policyholder to an agency location, or undefined when either
 * end lacks coordinates — a virtual office, or an account that never geocoded.
 */
export const distanceToAgency = (
  origin: unknown,
  agencyLocation: unknown,
  unit: DistanceUnit
): Distance | undefined => {
  if (!isGeoPoint(origin) || !isGeoPoint(agencyLocation)) {
    return undefined;
  }
  return { value: distanceBetween(origin, agencyLocation, unit), unit };
};

/** One decimal place, as the design shows it ("11.2 mi", "18.1 km"). */
export const formatDistance = (
  distance: Distance | undefined
): string | undefined => {
  if (typeof distance?.value !== 'number' || Number.isNaN(distance.value)) {
    return undefined;
  }
  return `${distance.value.toFixed(1)} ${distance.unit}`;
};
